"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FrameDataReader = void 0;
const readCoord_1 = require("./readCoord");
const readDelta_1 = require("./readDelta");
const BitReader_1 = require("../BitReader");
const Reader_1 = require("../Reader");
class FrameDataReader {
    static bad() {
        throw new Error('Invalid message type');
    }
    static nop() {
        return null;
    }
    static disconnect(r) {
        return {
            reason: r.str()
        };
    }
    static event(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let events = [];
        let eventCount = bs.readBits(5);
        for (let i = 0; i < eventCount; ++i) {
            let event = {
                index: bs.readBits(10)
            };
            let packetIndexBit = bs.readBits(1);
            if (packetIndexBit) {
                event.packetIndex = bs.readBits(11);
                let deltaBit = bs.readBits(1);
                if (deltaBit) {
                    event.delta = (0, readDelta_1.readDelta)(bs, deltaDecoders['event_t']);
                }
            }
            let fireTimeBit = bs.readBits(1);
            if (fireTimeBit) {
                event.fireTime = bs.readBits(16);
            }
            events.push(event);
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return { events };
    }
    static version(r) {
        return {
            version: r.ui()
        };
    }
    static setView(r) {
        return {
            entityIndex: r.s()
        };
    }
    static sound(r) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let flags = bs.readBits(9);
        let volume = 1;
        if ((flags & 1) !== 0) {
            volume = bs.readBits(8) / 255;
        }
        let attenuation = 1;
        if ((flags & 2) !== 0) {
            attenuation = bs.readBits(8) / 64;
        }
        let channel = bs.readBits(3);
        let entityIndex = bs.readBits(11);
        let soundIndex;
        if ((flags & 4) !== 0) {
            soundIndex = bs.readBits(16);
        }
        else {
            soundIndex = bs.readBits(8);
        }
        let xFlag = bs.readBits(1);
        let yFlag = bs.readBits(1);
        let zFlag = bs.readBits(1);
        let xPosition;
        let yPosition;
        let zPosition;
        if (xFlag) {
            xPosition = (0, readCoord_1.readCoord)(bs);
        }
        if (yFlag) {
            yPosition = (0, readCoord_1.readCoord)(bs);
        }
        if (zFlag) {
            zPosition = (0, readCoord_1.readCoord)(bs);
        }
        let pitch = 1;
        if ((flags & 8) !== 0) {
            pitch = bs.readBits(8);
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return {
            flags,
            volume,
            attenuation,
            channel,
            entityIndex,
            soundIndex,
            xPosition,
            yPosition,
            zPosition,
            pitch
        };
    }
    static time(r) {
        return {
            time: r.f()
        };
    }
    static print(r) {
        return {
            message: r.str()
        };
    }
    static stuffText(r) {
        let message = r.str();
        let commands = message.split(';').map(command => {
            let args = command
                .split(/\s*("[^"]+"|[^\s"]+)/)
                .map(arg => arg.replace(/^"(.*)"$/, '$1').trim())
                .filter(arg => arg);
            let func = args[0];
            let params = args.slice(1);
            return { func, params };
        });
        return { commands };
    }
    static setAngle(r) {
        return {
            pitch: r.s(),
            yaw: r.s(),
            roll: r.s()
        };
    }
    static serverInfo(r) {
        let info = {
            protocol: r.i(),
            spawnCount: r.i(),
            mapCrc: r.i(),
            clientDllHash: r.arrx(16, Reader_1.ReaderDataType.UByte),
            maxPlayers: r.ub(),
            playerIndex: r.ub(),
            isDeathmatch: r.ub(),
            gameDir: r.str(),
            hostName: r.str(),
            mapFileName: r.str(),
            mapCycle: r.str()
        };
        r.skip(1);
        return info;
    }
    static lightStyle(r) {
        return {
            index: r.ub(),
            lightInfo: r.str()
        };
    }
    static updateUserInfo(r) {
        return {
            clientIndex: r.ub(),
            clientUserId: r.ui(),
            clientUserInfo: r.str(),
            clientCdKeyHash: r.arrx(16, Reader_1.ReaderDataType.UByte)
        };
    }
    static deltaDescription(r, deltaDecoders) {
        let data = {
            name: r.str(),
            fields: []
        };
        let bs = new BitReader_1.BitStream(r.data.buffer);
        let fieldCount = r.us();
        bs.index = r.tell() * 8;
        for (let i = 0; i < fieldCount; ++i) {
            data.fields.push((0, readDelta_1.readDelta)(bs, deltaDecoders['delta_description_t']));
        }
        deltaDecoders[data.name] = data.fields;
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return data;
    }
    static clientData(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let deltaSequence = bs.readBits(1);
        if (deltaSequence) {
            bs.index += 8;
        }
        let clientDataDecoder = deltaDecoders['clientdata_t'];
        let clientData = (0, readDelta_1.readDelta)(bs, clientDataDecoder);
        let weaponDataDecoder = deltaDecoders['weapon_data_t'];
        while (bs.readBits(1)) {
            bs.index += 6;
            (0, readDelta_1.readDelta)(bs, weaponDataDecoder);
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return {
            clientData
        };
    }
    static stopSound(r) {
        return {
            entityIndex: r.s()
        };
    }
    static pings(r) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let pings = [];
        while (bs.readBits(1)) {
            pings.push({
                slot: bs.readBits(8),
                ping: bs.readBits(8),
                loss: bs.readBits(8)
            });
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return pings;
    }
    static particle(r) {
        return {
            position: [r.s() / 8, r.s() / 8, r.s() / 8],
            direction: [r.b(), r.b(), r.b()],
            count: r.ub(),
            color: r.ub()
        };
    }
    static damage() {
        return null;
    }
    static spawnStatic(r) {
        let data = {
            modelIndex: r.s(),
            sequence: r.b(),
            frame: r.b(),
            colorMap: r.s(),
            skin: r.b(),
            position: [],
            rotation: []
        };
        data.position[0] = r.s() / 8;
        data.rotation[0] = r.b() * (360 / 256);
        data.position[1] = r.s() / 8;
        data.rotation[1] = r.b() * (360 / 256);
        data.position[2] = r.s() / 8;
        data.rotation[2] = r.b() * (360 / 256);
        data.renderMode = r.b();
        if (data.renderMode) {
            data.renderAmt = r.b();
            data.renderColor = [r.ub(), r.ub(), r.ub()];
            data.renderFx = r.b();
        }
        return data;
    }
    static eventReliable(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let eventIndex = bs.readBits(10);
        let eventData = (0, readDelta_1.readDelta)(bs, deltaDecoders['event_t']);
        let delayBit = bs.readBits(1);
        let delay;
        if (delayBit) {
            delay = bs.readBits(16);
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return {
            eventIndex,
            eventData,
            delayBit,
            delay
        };
    }
    static spawnBaseLine(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let entities = [];
        while (true) {
            let entityIdx = bs.readBits(11);
            if (entityIdx === (1 << 11) - 1) {
                break;
            }
            let entityType = bs.readBits(2);
            let entityTypeString;
            if (entityType & 1) {
                if (entityIdx > 0 && entityIdx <= 32) {
                    entityTypeString = 'entity_state_player_t';
                }
                else {
                    entityTypeString = 'entity_state_t';
                }
            }
            else {
                entityTypeString = 'custom_entity_state_t';
            }
            entities[entityIdx] = (0, readDelta_1.readDelta)(bs, deltaDecoders[entityTypeString]);
        }
        let footer = bs.readBits(5);
        if (footer !== (1 << 5) - 1) {
            throw new Error('Bad spawnbaseline');
        }
        let nExtraData = bs.readBits(6);
        let extraData = [];
        for (let i = 0; i < nExtraData; ++i) {
            extraData.push((0, readDelta_1.readDelta)(bs, deltaDecoders['entity_state_t']));
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return {
            entities,
            extraData
        };
    }
    static tempEntity(r) {
        const TE_BEAMPOINTS = 0;
        const TE_BEAMENTPOINT = 1;
        const TE_GUNSHOT = 2;
        const TE_EXPLOSION = 3;
        const TE_TAREXPLOSION = 4;
        const TE_SMOKE = 5;
        const TE_TRACER = 6;
        const TE_LIGHTNING = 7;
        const TE_BEAMENTS = 8;
        const TE_SPARKS = 9;
        const TE_LAVASPLASH = 10;
        const TE_TELEPORT = 11;
        const TE_EXPLOSION2 = 12;
        const TE_BSPDECAL = 13;
        const TE_IMPLOSION = 14;
        const TE_SPRITETRAIL = 15;
        const TE_SPRITE = 17;
        const TE_BEAMSPRITE = 18;
        const TE_BEAMTORUS = 19;
        const TE_BEAMDISK = 20;
        const TE_BEAMCYLINDER = 21;
        const TE_BEAMFOLLOW = 22;
        const TE_GLOWSPRITE = 23;
        const TE_BEAMRING = 24;
        const TE_STREAK_SPLASH = 25;
        const TE_DLIGHT = 27;
        const TE_ELIGHT = 28;
        const TE_TEXTMESSAGE = 29;
        const TE_LINE = 30;
        const TE_BOX = 31;
        const TE_KILLBEAM = 99;
        const TE_LARGEFUNNEL = 100;
        const TE_BLOODSTREAM = 101;
        const TE_SHOWLINE = 102;
        const TE_BLOOD = 103;
        const TE_DECAL = 104;
        const TE_FIZZ = 105;
        const TE_MODEL = 106;
        const TE_EXPLODEMODEL = 107;
        const TE_BREAKMODEL = 108;
        const TE_GUNSHOTDECAL = 109;
        const TE_SPRITE_SPRAY = 110;
        const TE_ARMOR_RICOCHET = 111;
        const TE_PLAYERDECAL = 112;
        const TE_BUBBLES = 113;
        const TE_BUBBLETRAIL = 114;
        const TE_BLOODSPRITE = 115;
        const TE_WORLDDECAL = 116;
        const TE_WORLDDECALHIGH = 117;
        const TE_DECALHIGH = 118;
        const TE_PROJECTILE = 119;
        const TE_SPRAY = 120;
        const TE_PLAYERSPRITES = 121;
        const TE_PARTICLEBURST = 122;
        const TE_FIREFIELD = 123;
        const TE_PLAYERATTACHMENT = 124;
        const TE_KILLPLAYERATTACHMENTS = 125;
        const TE_MULTIGUNSHOT = 126;
        const TE_USERTRACER = 127;
        let type = r.ub();
        let data = {};
        switch (type) {
            case TE_BEAMPOINTS: {
                r.skip(24);
                break;
            }
            case TE_BEAMENTPOINT: {
                r.skip(20);
                break;
            }
            case TE_GUNSHOT: {
                r.skip(6);
                break;
            }
            case TE_EXPLOSION: {
                r.skip(11);
                break;
            }
            case TE_TAREXPLOSION: {
                r.skip(6);
                break;
            }
            case TE_SMOKE: {
                r.skip(10);
                break;
            }
            case TE_TRACER: {
                r.skip(12);
                break;
            }
            case TE_LIGHTNING: {
                r.skip(17);
                break;
            }
            case TE_BEAMENTS: {
                r.skip(16);
                break;
            }
            case TE_SPARKS: {
                r.skip(6);
                break;
            }
            case TE_LAVASPLASH: {
                r.skip(6);
                break;
            }
            case TE_TELEPORT: {
                r.skip(6);
                break;
            }
            case TE_EXPLOSION2: {
                r.skip(8);
                break;
            }
            case TE_BSPDECAL: {
                r.skip(8);
                let entityIndex = r.s();
                if (entityIndex) {
                    r.skip(2);
                }
                break;
            }
            case TE_IMPLOSION: {
                r.skip(9);
                break;
            }
            case TE_SPRITETRAIL: {
                r.skip(19);
                break;
            }
            case TE_SPRITE: {
                r.skip(10);
                break;
            }
            case TE_BEAMSPRITE: {
                r.skip(16);
                break;
            }
            case TE_BEAMTORUS: {
                r.skip(24);
                break;
            }
            case TE_BEAMDISK: {
                r.skip(24);
                break;
            }
            case TE_BEAMCYLINDER: {
                r.skip(24);
                break;
            }
            case TE_BEAMFOLLOW: {
                r.skip(10);
                break;
            }
            case TE_GLOWSPRITE: {
                r.skip(11);
                break;
            }
            case TE_BEAMRING: {
                r.skip(16);
                break;
            }
            case TE_STREAK_SPLASH: {
                r.skip(19);
                break;
            }
            case TE_DLIGHT: {
                r.skip(12);
                break;
            }
            case TE_ELIGHT: {
                r.skip(16);
                break;
            }
            case TE_TEXTMESSAGE: {
                data.channel = r.b();
                data.x = r.s();
                data.y = r.s();
                data.effect = r.b();
                data.textColor = [r.ub(), r.ub(), r.ub(), r.ub()];
                data.effectColor = [r.ub(), r.ub(), r.ub(), r.ub()];
                data.fadeInTime = r.s();
                data.fadeOutTime = r.s();
                data.holdTime = r.s();
                if (data.effect) {
                    data.effectTime = r.s();
                }
                data.message = r.str();
                break;
            }
            case TE_LINE: {
                r.skip(17);
                break;
            }
            case TE_BOX: {
                r.skip(17);
                break;
            }
            case TE_KILLBEAM: {
                r.skip(2);
                break;
            }
            case TE_LARGEFUNNEL: {
                r.skip(10);
                break;
            }
            case TE_BLOODSTREAM: {
                r.skip(14);
                break;
            }
            case TE_SHOWLINE: {
                r.skip(12);
                break;
            }
            case TE_BLOOD: {
                r.skip(14);
                break;
            }
            case TE_DECAL: {
                r.skip(9);
                break;
            }
            case TE_FIZZ: {
                r.skip(5);
                break;
            }
            case TE_MODEL: {
                r.skip(17);
                break;
            }
            case TE_EXPLODEMODEL: {
                r.skip(13);
                break;
            }
            case TE_BREAKMODEL: {
                r.skip(24);
                break;
            }
            case TE_GUNSHOTDECAL: {
                r.skip(9);
                break;
            }
            case TE_SPRITE_SPRAY: {
                r.skip(17);
                break;
            }
            case TE_ARMOR_RICOCHET: {
                r.skip(7);
                break;
            }
            case TE_PLAYERDECAL: {
                r.skip(10);
                break;
            }
            case TE_BUBBLES: {
                r.skip(19);
                break;
            }
            case TE_BUBBLETRAIL: {
                r.skip(19);
                break;
            }
            case TE_BLOODSPRITE: {
                r.skip(12);
                break;
            }
            case TE_WORLDDECAL: {
                r.skip(7);
                break;
            }
            case TE_WORLDDECALHIGH: {
                r.skip(7);
                break;
            }
            case TE_DECALHIGH: {
                r.skip(9);
                break;
            }
            case TE_PROJECTILE: {
                r.skip(16);
                break;
            }
            case TE_SPRAY: {
                r.skip(18);
                break;
            }
            case TE_PLAYERSPRITES: {
                r.skip(5);
                break;
            }
            case TE_PARTICLEBURST: {
                r.skip(10);
                break;
            }
            case TE_FIREFIELD: {
                r.skip(9);
                break;
            }
            case TE_PLAYERATTACHMENT: {
                r.skip(7);
                break;
            }
            case TE_KILLPLAYERATTACHMENTS: {
                r.skip(1);
                break;
            }
            case TE_MULTIGUNSHOT: {
                r.skip(18);
                break;
            }
            case TE_USERTRACER: {
                r.skip(15);
                break;
            }
            default: {
                throw new Error('Unknown temp entity type');
            }
        }
        return data;
    }
    static setPause(r) {
        return {
            isPaused: r.b()
        };
    }
    static signOnNum(r) {
        return {
            sign: r.b()
        };
    }
    static centerPrint(r) {
        return {
            message: r.str()
        };
    }
    static killedMonster() {
        return null;
    }
    static foundSecret() {
        return null;
    }
    static spawnStaticSound(r) {
        return {
            position: [r.s() / 8, r.s() / 8, r.s() / 8],
            soundIndex: r.us(),
            volume: r.ub() / 255,
            attenuation: r.ub() / 64,
            entityIndex: r.us(),
            pitch: r.ub(),
            flags: r.ub()
        };
    }
    static intermission() {
        return null;
    }
    static finale(r) {
        return {
            text: r.str()
        };
    }
    static cdTrack(r) {
        return {
            track: r.b(),
            loopTrack: r.b()
        };
    }
    static restore(r) {
        let saveName = r.str();
        let mapCount = r.ub();
        let maps = [];
        for (let i = 0; i < mapCount; ++i) {
            maps.push(r.str());
        }
        return { saveName, maps };
    }
    static cutscene(r) {
        return {
            text: r.str()
        };
    }
    static weaponAnim(r) {
        return {
            sequenceNumber: r.b(),
            weaponModelBodyGroup: r.b()
        };
    }
    static decalName(r) {
        return {
            positionIndex: r.ub(),
            decalName: r.str()
        };
    }
    static roomType(r) {
        return {
            type: r.us()
        };
    }
    static addAngle(r) {
        return {
            angleToAdd: r.s() / (360 / 65536)
        };
    }
    static newUserMsg(r) {
        return {
            index: r.ub(),
            size: r.b(),
            name: r.nstr(16)
        };
    }
    static packetEntities(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let entityStates = [];
        bs.readBits(16);
        let entityNumber = 0;
        while (true) {
            let footer = bs.readBits(16);
            if (footer === 0) {
                break;
            }
            bs.index -= 16;
            let entityNumberIncrement = bs.readBits(1);
            if (!entityNumberIncrement) {
                let absoluteEntityNumber = bs.readBits(1);
                if (absoluteEntityNumber) {
                    entityNumber = bs.readBits(11);
                }
                else {
                    entityNumber += bs.readBits(6);
                }
            }
            else {
                entityNumber++;
            }
            let custom = bs.readBits(1);
            let useBaseline = bs.readBits(1);
            if (useBaseline) {
                bs.index += 6;
            }
            let entityType = 'entity_state_t';
            if (entityNumber > 0 && entityNumber <= 32) {
                entityType = 'entity_state_player_t';
            }
            else if (custom) {
                entityType = 'custom_entity_state_t';
            }
            entityStates.push((0, readDelta_1.readDelta)(bs, deltaDecoders[entityType]));
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return { entityStates };
    }
    static deltaPacketEntities(r, deltaDecoders) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        bs.readBits(16);
        bs.index += 8;
        let entityStates = [];
        let entityIdx = 0;
        while (true) {
            let footer = bs.readBits(16);
            if (footer === 0) {
                break;
            }
            bs.index -= 16;
            let removeEntity = bs.readBits(1);
            let absoluteEntityNumber = bs.readBits(1);
            if (absoluteEntityNumber) {
                entityIdx = bs.readBits(11);
            }
            else {
                entityIdx += bs.readBits(6);
            }
            if (removeEntity) {
                continue;
            }
            let custom = bs.readBits(1);
            let entityType = 'entity_state_t';
            if (entityIdx > 0 && entityIdx < 32) {
                entityType = 'entity_state_player_t';
            }
            else if (custom) {
                entityType = 'custom_entity_state_t';
            }
            entityStates[entityIdx] = (0, readDelta_1.readDelta)(bs, deltaDecoders[entityType]);
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return { entityStates };
    }
    static choke() {
        return null;
    }
    static resourceList(r) {
        let bs = new BitReader_1.BitStream(r.data.buffer);
        bs.index = r.tell() * 8;
        let entries = [];
        let entryCount = bs.readBits(12);
        for (let i = 0; i < entryCount; ++i) {
            let entry = {};
            entry.type = bs.readBits(4);
            entry.name = bs.readString();
            entry.index = bs.readBits(12);
            entry.size = bs.readBits(24);
            let flags = bs.readBits(3);
            if (flags & 4) {
                bs.index += 128;
            }
            if (bs.readBits(1)) {
                bs.index += 256;
            }
            entries.push(entry);
        }
        if (bs.readBits(1)) {
            while (bs.readBits(1)) {
                let nBits = bs.readBits(1) ? 5 : 10;
                bs.index += nBits;
            }
        }
        if (bs.index % 8 > 0) {
            r.seek(Math.floor(bs.index / 8) + 1);
        }
        else {
            r.seek(bs.index / 8);
        }
        return entries;
    }
    static newMoveVars(r) {
        return {
            gravity: r.f(),
            stopSpeed: r.f(),
            maxSpeed: r.f(),
            spectatorMaxSpeed: r.f(),
            acceleration: r.f(),
            airAcceleration: r.f(),
            waterAcceleration: r.f(),
            friction: r.f(),
            edgeFriction: r.f(),
            waterFriction: r.f(),
            entityGravity: r.f(),
            bounce: r.f(),
            stepSize: r.f(),
            maxVelocity: r.f(),
            zMax: r.f(),
            waveHeight: r.f(),
            footsteps: r.b(),
            rollAngle: r.f(),
            rollSpeed: r.f(),
            skyColor: [r.f(), r.f(), r.f()],
            skyVec: [r.f(), r.f(), r.f()],
            skyName: r.str()
        };
    }
    static resourceRequest(r) {
        let data = {
            spawnCount: r.i()
        };
        r.skip(4);
        return data;
    }
    static customization(r) {
        let playerIndex = r.ub();
        let type = r.ub();
        let name = r.str();
        let index = r.us();
        let downloadSize = r.ui();
        let flags = r.ub();
        let md5hash;
        if (flags & 4) {
            md5hash = [r.i(), r.i(), r.i(), r.i()];
        }
        return {
            playerIndex,
            type,
            name,
            index,
            downloadSize,
            flags,
            md5hash
        };
    }
    static crosshairAngle(r) {
        return {
            pitch: r.b(),
            yaw: r.b()
        };
    }
    static soundFade(r) {
        return {
            initialPercent: r.ub(),
            holdTime: r.ub(),
            fadeOutTime: r.ub(),
            fadeInTime: r.ub()
        };
    }
    static fileTxferFailed(r) {
        return {
            filename: r.str()
        };
    }
    static hltv(r) {
        return {
            mode: r.ub()
        };
    }
    static director(r) {
        let length = r.ub();
        return {
            flag: r.ub(),
            message: r.nstr(length - 1)
        };
    }
    static voiceInit(r) {
        return {
            codecName: r.str(),
            quality: r.b()
        };
    }
    static voiceData(r) {
        let playerIndex = r.ub();
        let size = r.us();
        let data = r.arrx(size, Reader_1.ReaderDataType.UByte);
        return { playerIndex, data };
    }
    static sendExtraInfo(r) {
        return {
            fallbackDir: r.str(),
            canCheat: r.ub()
        };
    }
    static timeScale(r) {
        return {
            timeScale: r.f()
        };
    }
    static resourceLocation(r) {
        return {
            url: r.str()
        };
    }
    static sendCvarValue(r) {
        return {
            name: r.str()
        };
    }
    static sendCvarValue2(r) {
        return {
            requestId: r.ui(),
            name: r.str()
        };
    }
    static read(r, type, deltaDecoders) {
        if (type === 0) {
            return null;
        }
        const handler = FrameDataReader.handlers[type];
        if (handler) {
            return handler(r, deltaDecoders);
        }
        else {
            return null;
        }
    }
}
exports.FrameDataReader = FrameDataReader;
FrameDataReader.handlers = [
    FrameDataReader.bad,
    FrameDataReader.nop,
    FrameDataReader.disconnect,
    FrameDataReader.event,
    FrameDataReader.version,
    FrameDataReader.setView,
    FrameDataReader.sound,
    FrameDataReader.time,
    FrameDataReader.print,
    FrameDataReader.stuffText,
    FrameDataReader.setAngle,
    FrameDataReader.serverInfo,
    FrameDataReader.lightStyle,
    FrameDataReader.updateUserInfo,
    FrameDataReader.deltaDescription,
    FrameDataReader.clientData,
    FrameDataReader.stopSound,
    FrameDataReader.pings,
    FrameDataReader.particle,
    FrameDataReader.damage,
    FrameDataReader.spawnStatic,
    FrameDataReader.eventReliable,
    FrameDataReader.spawnBaseLine,
    FrameDataReader.tempEntity,
    FrameDataReader.setPause,
    FrameDataReader.signOnNum,
    FrameDataReader.centerPrint,
    FrameDataReader.killedMonster,
    FrameDataReader.foundSecret,
    FrameDataReader.spawnStaticSound,
    FrameDataReader.intermission,
    FrameDataReader.finale,
    FrameDataReader.cdTrack,
    FrameDataReader.restore,
    FrameDataReader.cutscene,
    FrameDataReader.weaponAnim,
    FrameDataReader.decalName,
    FrameDataReader.roomType,
    FrameDataReader.addAngle,
    FrameDataReader.newUserMsg,
    FrameDataReader.packetEntities,
    FrameDataReader.deltaPacketEntities,
    FrameDataReader.choke,
    FrameDataReader.resourceList,
    FrameDataReader.newMoveVars,
    FrameDataReader.resourceRequest,
    FrameDataReader.customization,
    FrameDataReader.crosshairAngle,
    FrameDataReader.soundFade,
    FrameDataReader.fileTxferFailed,
    FrameDataReader.hltv,
    FrameDataReader.director,
    FrameDataReader.voiceInit,
    FrameDataReader.voiceData,
    FrameDataReader.sendExtraInfo,
    FrameDataReader.timeScale,
    FrameDataReader.resourceLocation,
    FrameDataReader.sendCvarValue,
    FrameDataReader.sendCvarValue2
];
(function (FrameDataReader) {
    let SVC;
    (function (SVC) {
        SVC[SVC["BAD"] = 0] = "BAD";
        SVC[SVC["NOP"] = 1] = "NOP";
        SVC[SVC["DISCONNECT"] = 2] = "DISCONNECT";
        SVC[SVC["EVENT"] = 3] = "EVENT";
        SVC[SVC["VERSION"] = 4] = "VERSION";
        SVC[SVC["SETVIEW"] = 5] = "SETVIEW";
        SVC[SVC["SOUND"] = 6] = "SOUND";
        SVC[SVC["TIME"] = 7] = "TIME";
        SVC[SVC["PRINT"] = 8] = "PRINT";
        SVC[SVC["STUFFTEXT"] = 9] = "STUFFTEXT";
        SVC[SVC["SETANGLE"] = 10] = "SETANGLE";
        SVC[SVC["SERVERINFO"] = 11] = "SERVERINFO";
        SVC[SVC["LIGHTSTYLE"] = 12] = "LIGHTSTYLE";
        SVC[SVC["UPDATEUSERINFO"] = 13] = "UPDATEUSERINFO";
        SVC[SVC["DELTADESCRIPTION"] = 14] = "DELTADESCRIPTION";
        SVC[SVC["CLIENTDATA"] = 15] = "CLIENTDATA";
        SVC[SVC["STOPSOUND"] = 16] = "STOPSOUND";
        SVC[SVC["PINGS"] = 17] = "PINGS";
        SVC[SVC["PARTICLE"] = 18] = "PARTICLE";
        SVC[SVC["DAMAGE"] = 19] = "DAMAGE";
        SVC[SVC["SPAWNSTATIC"] = 20] = "SPAWNSTATIC";
        SVC[SVC["EVENT_RELIABLE"] = 21] = "EVENT_RELIABLE";
        SVC[SVC["SPAWNBASELINE"] = 22] = "SPAWNBASELINE";
        SVC[SVC["TEMPENTITY"] = 23] = "TEMPENTITY";
        SVC[SVC["SETPAUSE"] = 24] = "SETPAUSE";
        SVC[SVC["SIGNONNUM"] = 25] = "SIGNONNUM";
        SVC[SVC["CENTERPRINT"] = 26] = "CENTERPRINT";
        SVC[SVC["KILLEDMONSTER"] = 27] = "KILLEDMONSTER";
        SVC[SVC["FOUNDSECRET"] = 28] = "FOUNDSECRET";
        SVC[SVC["SPAWNSTATICSOUND"] = 29] = "SPAWNSTATICSOUND";
        SVC[SVC["INTERMISSION"] = 30] = "INTERMISSION";
        SVC[SVC["FINALE"] = 31] = "FINALE";
        SVC[SVC["CDTRACK"] = 32] = "CDTRACK";
        SVC[SVC["RESTORE"] = 33] = "RESTORE";
        SVC[SVC["CUTSCENE"] = 34] = "CUTSCENE";
        SVC[SVC["WEAPONANIM"] = 35] = "WEAPONANIM";
        SVC[SVC["DECALNAME"] = 36] = "DECALNAME";
        SVC[SVC["ROOMTYPE"] = 37] = "ROOMTYPE";
        SVC[SVC["ADDANGLE"] = 38] = "ADDANGLE";
        SVC[SVC["NEWUSERMSG"] = 39] = "NEWUSERMSG";
        SVC[SVC["PACKETENTITIES"] = 40] = "PACKETENTITIES";
        SVC[SVC["DELTAPACKETENTITIES"] = 41] = "DELTAPACKETENTITIES";
        SVC[SVC["CHOKE"] = 42] = "CHOKE";
        SVC[SVC["RESOURCELIST"] = 43] = "RESOURCELIST";
        SVC[SVC["NEWMOVEVARS"] = 44] = "NEWMOVEVARS";
        SVC[SVC["RESOURCEREQUEST"] = 45] = "RESOURCEREQUEST";
        SVC[SVC["CUSTOMIZATION"] = 46] = "CUSTOMIZATION";
        SVC[SVC["CROSSHAIRANGLE"] = 47] = "CROSSHAIRANGLE";
        SVC[SVC["SOUNDFADE"] = 48] = "SOUNDFADE";
        SVC[SVC["FILETXFERFAILED"] = 49] = "FILETXFERFAILED";
        SVC[SVC["HLTV"] = 50] = "HLTV";
        SVC[SVC["DIRECTOR"] = 51] = "DIRECTOR";
        SVC[SVC["VOICEINIT"] = 52] = "VOICEINIT";
        SVC[SVC["VOICEDATA"] = 53] = "VOICEDATA";
        SVC[SVC["SENDEXTRAINFO"] = 54] = "SENDEXTRAINFO";
        SVC[SVC["TIMESCALE"] = 55] = "TIMESCALE";
        SVC[SVC["RESOURCELOCATION"] = 56] = "RESOURCELOCATION";
        SVC[SVC["SENDCVARVALUE"] = 57] = "SENDCVARVALUE";
        SVC[SVC["SENDCVARVALUE2"] = 58] = "SENDCVARVALUE2";
    })(SVC = FrameDataReader.SVC || (FrameDataReader.SVC = {}));
})(FrameDataReader || (exports.FrameDataReader = FrameDataReader = {}));
